import React from 'react';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Keyboard } from 'swiper';
import { Flex } from '../';
import useCarouselNav, { CarouselNavButtons } from './useCarouselNav';

const MAX_SLIDE_WIDTH = 375;
const HORIZONTAL_PADDING = 16 * 0; //removed horizontal padding for now

const SwiperStyled = styled(Swiper)`
  padding: 0 0 10px;

  .swiper-button-prev,
  .swiper-button-next {
    display: none;
  }

  &.swiper-horizontal {
    margin: unset;
  }

  .swiper-slide {
    height: auto;
    display: flex;

    & > * {
      flex: 1;
    }
  }
`;

const SwiperContainer = styled.div`
  width: 100%;
  max-width: ${({ $maxWidth }) => $maxWidth}px;
  align-self: flex-start;
`;

/**
 * ListCarousel — higher-level wrapper for horizontal card lists.
 *
 * Renders a header row (left: header, right: nav buttons + headerRight)
 * and a Swiper underneath. Each child becomes a SwiperSlide.
 *
 * For custom layouts, use `useCarouselNav` hook directly instead.
 */
const ListCarousel = ({ header, headerRight, children, spaceBetween = 30, navColor, ...swiperProps }) => {
  const { navProps, swiperNavProps } = useCarouselNav();
  const count = React.Children.count(children);
  const maxVisible = Math.min(3, count);
  const maxContainerWidth = maxVisible * MAX_SLIDE_WIDTH + (maxVisible - 1) * spaceBetween + HORIZONTAL_PADDING;

  return (
    <Flex width='100%' direction='column' gap='20'>
      <Flex align='center' justify='space-between' width='100%'>
        <Flex align='center' gap='5'>
          {header}
        </Flex>
        <Flex gap='8' align='center'>
          <CarouselNavButtons {...navProps} color={navColor} />
          {headerRight}
        </Flex>
      </Flex>
      <SwiperContainer $maxWidth={maxContainerWidth}>
        <SwiperStyled
          key={count}
          modules={[Navigation, Keyboard]}
          keyboard={{ enabled: true }}
          {...swiperNavProps}
          slidesPerView={1}
          spaceBetween={spaceBetween}
          breakpoints={{
            600: { slidesPerView: Math.min(2, count) },
            900: { slidesPerView: Math.min(3, count) },
          }}
          {...swiperProps}
        >
          {React.Children.map(children, (child, i) => (
            <SwiperSlide key={i}>{child}</SwiperSlide>
          ))}
        </SwiperStyled>
      </SwiperContainer>
    </Flex>
  );
};

export default ListCarousel;
